import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ADMIN_BASE } from "../config";
import { Flame, Clock, LogOut, ShieldCheck } from "lucide-react";
import InstallButton from "../components/InstallButton";

export default function Portal() {
    const { admin, logout } = useAuth();

    const signIn = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + ADMIN_BASE;
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    if (admin === null) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#070c18] text-slate-300/60">
                Loading…
            </div>
        );
    }

    if (admin && admin.status === "approved") {
        return <Navigate to="/admin" replace />;
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-[#070c18] px-4">
            <div className="grain-overlay" aria-hidden="true" />
            <div className="relative w-full max-w-md">
                <span className="pointer-events-none absolute -left-5 top-1/2 hidden h-44 w-11 -translate-y-1/2 rounded-[1.9rem] bg-[#0c172e] ring-1 ring-blue-500/20 sm:block" />
                <span className="pointer-events-none absolute -right-5 top-1/2 hidden h-44 w-11 -translate-y-1/2 rounded-[1.9rem] bg-[#0c172e] ring-1 ring-blue-500/20 sm:block" />
                <div className="relative rounded-[2rem] border border-blue-500/20 bg-[#0c172e] trillian-panel p-9">
                <div className="flex items-center gap-3">
                    <span className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-b from-[#1e3a8a] to-[#0a1326] ring-1 ring-blue-400/40">
                        <img
                            src="/images/sprite-neuro.png"
                            alt="Professor Neuro"
                            className="h-9 w-9 rounded-xl object-cover neuro-float"
                        />
                        <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-emerald-400 text-emerald-400 led-pulse ring-2 ring-[#0c172e]" />
                    </span>
                    <div>
                        <h1 className="font-serif text-2xl text-blue-50">Team Control Panel</h1>
                        <p className="text-xs text-slate-300/50">
                            NeuroPlay Counseling — private access
                        </p>
                    </div>
                </div>

                {admin && admin.status === "pending" ? (
                    <div className="mt-8" data-testid="portal-pending">
                        <div className="flex items-start gap-3 rounded-2xl border border-amber-400/25 bg-amber-400/5 p-4">
                            <Clock className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-300" />
                            <div>
                                <p className="font-semibold text-blue-50">Access pending approval</p>
                                <p className="mt-1 text-sm leading-relaxed text-slate-300/70">
                                    Thanks, <span className="text-sky-300">{admin.email}</span>.
                                    An administrator needs to approve your account before you can
                                    manage the site. You'll be able to sign in as soon as you're
                                    approved.
                                </p>
                            </div>
                        </div>
                        <button
                            data-testid="portal-signout"
                            onClick={logout}
                            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full border border-blue-500/30 px-6 py-3 text-sm text-slate-300 hover:bg-blue-500/10"
                        >
                            <LogOut className="h-4 w-4" /> Sign out
                        </button>
                    </div>
                ) : (
                    <div className="mt-8">
                        <p className="text-sm leading-relaxed text-slate-300/70">
                            Sign in with your Google account to manage the website. New team members
                            will be granted access once an administrator approves them.
                        </p>
                        <button
                            data-testid="portal-google-signin"
                            onClick={signIn}
                            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-b from-blue-400 via-blue-500 to-blue-700 px-6 py-3.5 text-sm font-bold text-white ring-1 ring-blue-300/30 hover:brightness-110"
                        >
                            <ShieldCheck className="h-4 w-4" /> Sign in with Google
                        </button>
                        <p className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-slate-300/40">
                            <Flame className="h-3 w-3 text-amber-400/70" /> Every change is recorded
                            under your name in History.
                        </p>
                    </div>
                )}

                <div className="mt-7 flex items-center justify-between border-t border-blue-500/10 pt-5">
                    <span className="flex items-center gap-1.5" aria-hidden="true">
                        <span className="grid h-6 w-6 place-items-center rounded-md bg-[#0a1326] text-xs text-sky-400 ring-1 ring-blue-500/20">+</span>
                        <span className="h-2.5 w-2.5 rounded-full bg-rose-400/80" />
                        <span className="h-2.5 w-2.5 rounded-full bg-amber-300/80" />
                        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
                        <span className="h-2.5 w-2.5 rounded-full bg-sky-400/80" />
                    </span>
                    <InstallButton variant="admin" />
                </div>
                </div>
            </div>
        </div>
    );
}
